import apiClient from './api';
import {
  InsuranceProduct,
  InsuranceProductCreate,
  InsuranceProductUpdate,
  InsuranceProductCreateEnhanced,
  InsuranceProductUpdateEnhanced,
  ProductAttachment,
  PaginatedResponse,
  InsuranceType,
  InsuranceCategory,
  InsuranceCompany,
} from '../types';

class ProductService {
  private readonly basePath = '/products';

  /**
   * Get list of products with pagination, search, and filters
   */
  async getProducts(
    skip: number = 0,
    limit: number = 50,
    search?: string,
    insuranceType?: InsuranceType,
    companyId?: string
  ): Promise<PaginatedResponse<InsuranceProduct>> {
    const params: any = { skip, limit };
    if (search) {
      params.search = search;
    }
    if (insuranceType) {
      params.insurance_type = insuranceType;
    }
    if (companyId) {
      params.company_id = companyId;
    }

    const response = await apiClient.get<PaginatedResponse<InsuranceProduct>>(
      this.basePath,
      { params }
    );
    return response.data;
  }

  /**
   * Get list of products filtered by category (NEW)
   */
  async getProductsByCategory(
    skip: number = 0,
    limit: number = 50,
    search?: string,
    insuranceCategory?: InsuranceCategory,
    companyId?: string
  ): Promise<PaginatedResponse<InsuranceProduct>> {
    const params: any = { skip, limit };
    if (search) {
      params.search = search;
    }
    if (insuranceCategory) {
      params.insurance_category = insuranceCategory;
    }
    if (companyId) {
      params.company_id = companyId;
    }

    const response = await apiClient.get<PaginatedResponse<InsuranceProduct>>(
      this.basePath,
      { params }
    );
    return response.data;
  }

  /**
   * Get companies filtered by insurance category (NEW)
   */
  async getCompaniesByCategory(category: InsuranceCategory): Promise<InsuranceCompany[]> {
    const response = await apiClient.get<InsuranceCompany[]>(
      `/companies/by-category/${category}`
    );
    return response.data;
  }

  /**
   * Get single product by ID
   */
  async getProduct(id: string): Promise<InsuranceProduct> {
    const response = await apiClient.get<InsuranceProduct>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create new product (legacy - for backward compatibility)
   */
  async createProduct(data: InsuranceProductCreate): Promise<InsuranceProduct> {
    const response = await apiClient.post<InsuranceProduct>(this.basePath, data);
    return response.data;
  }

  /**
   * Create new product with enhanced schema (NEW)
   */
  async createProductEnhanced(data: InsuranceProductCreateEnhanced): Promise<InsuranceProduct> {
    const response = await apiClient.post<InsuranceProduct>(this.basePath, data);
    return response.data;
  }

  /**
   * Update existing product (legacy - for backward compatibility)
   */
  async updateProduct(id: string, data: InsuranceProductUpdate): Promise<InsuranceProduct> {
    const response = await apiClient.put<InsuranceProduct>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Update existing product with enhanced schema (NEW)
   */
  async updateProductEnhanced(id: string, data: InsuranceProductUpdateEnhanced): Promise<InsuranceProduct> {
    const response = await apiClient.put<InsuranceProduct>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete product (soft delete)
   */
  async deleteProduct(id: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Upload file attachment to product (NEW)
   */
  async uploadAttachment(productId: string, file: File): Promise<ProductAttachment> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<ProductAttachment>(
      `${this.basePath}/${productId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  /**
   * Delete file attachment from product (NEW)
   */
  async deleteAttachment(productId: string, attachmentId: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/${productId}/attachments/${attachmentId}`);
  }

  /**
   * List all attachments for a product
   */
  async listAttachments(productId: string): Promise<ProductAttachment[]> {
    const response = await apiClient.get<ProductAttachment[]>(
      `${this.basePath}/${productId}/attachments`
    );
    return response.data;
  }

  /**
   * Download file attachment from product (NEW)
   */
  async downloadAttachment(productId: string, attachmentId: string): Promise<Blob> {
    const response = await apiClient.get(
      `${this.basePath}/${productId}/attachments/${attachmentId}/download`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  /**
   * View file attachment inline (opens in browser)
   */
  async viewAttachment(productId: string, attachmentId: string): Promise<Blob> {
    const response = await apiClient.get(
      `${this.basePath}/${productId}/attachments/${attachmentId}/download`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  async downloadImportTemplate(): Promise<void> {
    const response = await apiClient.get(`${this.basePath}/import-template`, { responseType: 'blob' });
    _downloadBlob(response.data, 'product_import_template.xlsx');
  }

  async importFromFile(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${this.basePath}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async exportToExcel(params?: { search?: string; company_id?: string; insurance_category?: string }): Promise<void> {
    const response = await apiClient.get(`${this.basePath}/export`, { params, responseType: 'blob' });
    _downloadBlob(response.data, 'products_export.xlsx');
  }

  async retryImportRow(rawData: Record<string, unknown>): Promise<any> {
    const formData = new FormData();
    const headers  = Object.keys(rawData).join(',');
    const values   = Object.values(rawData).map(v => String(v ?? '')).join(',');
    const blob     = new Blob([`${headers}\n${values}`], { type: 'text/csv' });
    formData.append('file', blob, 'retry.csv');
    const response = await apiClient.post(`${this.basePath}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.results?.[0] ?? response.data;
  }
}

function _downloadBlob(blob: Blob, filename: string): void {
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href  = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

const productService = new ProductService();
export default productService;
